import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  Dumbbell,
  HeartPulse,
  Footprints,
  Salad,
  Stethoscope,
  Users,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useWorkouts } from '@/hooks/useWorkouts';
import { useHealth } from '@/hooks/useHealth';
import { useBody } from '@/hooks/useBody';
import { useCardioGoals } from '@/hooks/useCardioGoals';
import { useSectionGoal } from '@/hooks/useSectionGoal';
import { HeroTicker } from '@/components/common/HeroTicker';
import { MobileHome } from '@/components/home/MobileHome';
import { MobileGuest } from '@/components/home/MobileGuest';
import { STEPS_GOAL } from '@/types/Health';
import { formatDate, formatInt } from '@/utils/format';
import type { Workout } from '@/types/Workout';

export function HomePage() {
  const { user, loading } = useAuth();
  const isMobile = useIsMobile();
  const isAuth = !loading && !!user;

  // Mobile → cockpit (authed) or guest landing; desktop below.
  if (isMobile) {
    if (loading) return <div className="mhome" style={{ minHeight: '60vh' }} />;
    return isAuth ? <MobileHome name={user?.name || user?.email || 'User'} /> : <MobileGuest />;
  }

  if (!isAuth) return <DesktopGuest />;

  // Берём первое слово из user.name (например, "Иван Петров" → "Иван").
  const heroName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';
  return <DesktopCockpit heroName={heroName} />;
}

/* ---------------- Guest landing ---------------- */

const GUEST_FEATURES = [
  {
    icon: Activity,
    title: 'Cardio tracking',
    text: 'Runs, rides and swims — log them by hand or sync straight from Strava.',
  },
  {
    icon: Dumbbell,
    title: 'Strength log',
    text: 'Exercises, sets, reps and weights with volume totals per session.',
  },
  {
    icon: HeartPulse,
    title: 'Body & health',
    text: 'Weight, measurements and daily wellness — sleep, HR, steps, stress.',
  },
  {
    icon: Users,
    title: 'Feed & friends',
    text: 'Follow training partners, react and comment on their workouts.',
  },
];

function DesktopGuest() {
  const navigate = useNavigate();
  return (
    <>
      <section className="hero">
        <div className="hero-content" id="hero-guest">
          <h1>
            Train harder.
            <br />
            Win more.
          </h1>
          <button className="btn-accent" onClick={() => navigate('/login')}>
            Get started →
          </button>
        </div>
      </section>

      <div className="features">
        {GUEST_FEATURES.map(({ icon: Icon, title, text }) => (
          <div className="card" key={title}>
            <i>
              <Icon size={28} strokeWidth={1.5} />
            </i>
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </>
  );
}

/* ---------------- Authed cockpit ---------------- */

function startOfWeek(now: Date): Date {
  const day = (now.getDay() + 6) % 7; // Mon=0
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(now.getDate() - day);
  return d;
}

function strengthVolumeKg(w: Workout): number {
  let v = 0;
  for (const ex of w.exercises ?? []) {
    for (const s of ex.sets ?? []) v += (s.reps ?? 0) * (s.weightKg ?? 0);
  }
  return v;
}

/** Progress ring reusing the .goal-ring CSS (dasharray 175.9 = 2π·28). */
function GoalRing({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(1, pct));
  return (
    <div className="goal-ring-wrap" aria-hidden="true">
      <svg className="goal-ring" viewBox="0 0 60 60">
        <circle className="goal-ring-bg" cx="30" cy="30" r="28" />
        <circle
          className="goal-ring-fill"
          cx="30"
          cy="30"
          r="28"
          style={{ strokeDashoffset: 175.9 * (1 - clamped) }}
        />
      </svg>
      <div className="goal-ring-percent">{Math.round(clamped * 100)}%</div>
    </div>
  );
}

interface GoalCardProps {
  to: string;
  icon: typeof Activity;
  title: string;
  pct: number;
  values: React.ReactNode;
  link: string;
}

function GoalCard({ to, icon: Icon, title, pct, values, link }: GoalCardProps) {
  return (
    <Link className="goal-overview-card" to={to}>
      <GoalRing pct={pct} />
      <div className="goal-overview-info">
        <div className="goal-overview-title-row">
          <i>
            <Icon size={13} strokeWidth={2} />
          </i>
          {title}
        </div>
        <div className="goal-overview-values">{values}</div>
        <div className="goal-overview-link">{link} →</div>
      </div>
    </Link>
  );
}

function DesktopCockpit({ heroName }: { heroName: string }) {
  const { workouts } = useWorkouts(true);
  const { metrics } = useHealth(true);
  const { latest: body, targets } = useBody();

  const now = useMemo(() => new Date(), []);
  const weekStart = useMemo(() => startOfWeek(now), [now]);
  const monthStart = useMemo(() => new Date(now.getFullYear(), now.getMonth(), 1), [now]);

  const sorted = useMemo(
    () => [...workouts].sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? '')),
    [workouts],
  );
  const last = sorted[0];

  const week = useMemo(
    () => sorted.filter((w) => w.startTime != null && new Date(w.startTime) >= weekStart),
    [sorted, weekStart],
  );
  const month = useMemo(
    () => sorted.filter((w) => w.startTime != null && new Date(w.startTime) >= monthStart),
    [sorted, monthStart],
  );

  const weekCardio = week.filter((w) => w.modality !== 'STRENGTH');
  const weekStrength = week.filter((w) => w.modality === 'STRENGTH');
  const weekKm = weekCardio.reduce((s, w) => s + (w.distance ?? 0), 0);
  const weekVolT = weekStrength.reduce((s, w) => s + strengthVolumeKg(w), 0) / 1000;

  const monthCardio = month.filter((w) => w.modality !== 'STRENGTH').length;
  const monthStrength = month.filter((w) => w.modality === 'STRENGTH').length;

  const { goals: cardioGoals } = useCardioGoals(monthCardio);
  const { goal: strengthGoal } = useSectionGoal('strength');
  const { goal: dietGoal } = useSectionGoal('diet');
  const { goal: medicalGoal } = useSectionGoal('medical');

  const latestHealth = useMemo(
    () => [...metrics].sort((a, b) => (b.metricDate ?? '').localeCompare(a.metricDate ?? ''))[0],
    [metrics],
  );
  const steps = latestHealth?.steps ?? null;

  const weight = body.weight ?? null;
  const targetWeight = targets.weight ?? null;
  // "Closeness" to target — honest for both cutting and bulking.
  const weightPct =
    weight != null && targetWeight != null && Math.max(weight, targetWeight) > 0
      ? Math.min(weight, targetWeight) / Math.max(weight, targetWeight)
      : 0;

  const ticker = useMemo(() => {
    const items: string[] = [];
    if (week.length > 0) {
      items.push(
        `This week · ${week.length} workout${week.length === 1 ? '' : 's'}` +
          (weekKm > 0 ? ` · ${weekKm.toFixed(1)} km cardio` : '') +
          (weekStrength.length > 0 ? ` · ${weekStrength.length} strength` : ''),
      );
    } else {
      items.push('This week · nothing logged yet — today is a good day to start');
    }
    if (last?.startTime) {
      const lastName =
        last.name ||
        last.workoutTypeName ||
        (last.modality === 'STRENGTH' ? 'Strength' : 'Workout');
      items.push(`Last session · ${lastName} · ${formatDate(last.startTime)}`);
    }
    if (weight != null) {
      items.push(
        `Body · ${weight.toFixed(1)} kg` +
          (targetWeight != null ? ` · target ${targetWeight.toFixed(1)} kg` : ''),
      );
    }
    if (steps != null) {
      items.push(
        `Steps · ${formatInt(steps)} of ${formatInt(STEPS_GOAL)} on ${latestHealth?.metricDate ?? ''}`,
      );
    }
    return items;
  }, [week.length, weekKm, weekStrength.length, last, weight, targetWeight, steps, latestHealth]);

  return (
    <>
      <section className="hero">
        <div className="hero-content" id="hero-auth">
          <h1>
            Welcome back,
            <br />
            <span style={{ color: 'var(--accent)' }}>{heroName}</span>
          </h1>
        </div>
      </section>

      <HeroTicker items={ticker} badge="TODAY" />

      <section className="goals-overview">
        <h2>Goals</h2>
        <div className="goals-grid">
          <GoalCard
            to="/cardio"
            icon={Activity}
            title="Cardio"
            pct={cardioGoals.sessions > 0 ? monthCardio / cardioGoals.sessions : 0}
            values={
              <>
                <span className="goal-overview-current">{monthCardio}</span>
                <span className="goal-overview-sep">/</span>
                {cardioGoals.sessions}
                <span className="goal-overview-unit">sessions this month</span>
              </>
            }
            link="Cardio"
          />
          <GoalCard
            to="/strength"
            icon={Dumbbell}
            title="Strength"
            pct={strengthGoal.target > 0 ? monthStrength / strengthGoal.target : 0}
            values={
              <>
                <span className="goal-overview-current">{monthStrength}</span>
                <span className="goal-overview-sep">/</span>
                {strengthGoal.target}
                <span className="goal-overview-unit">workouts this month</span>
              </>
            }
            link="Strength"
          />
          <GoalCard
            to="/body"
            icon={HeartPulse}
            title="Weight"
            pct={weightPct}
            values={
              weight != null ? (
                <>
                  <span className="goal-overview-current">{weight.toFixed(1)}</span>
                  {targetWeight != null && (
                    <>
                      <span className="goal-overview-sep">→</span>
                      {targetWeight.toFixed(1)}
                    </>
                  )}
                  <span className="goal-overview-unit">kg</span>
                </>
              ) : (
                'No measurements yet'
              )
            }
            link="Body"
          />
          <GoalCard
            to="/health"
            icon={Footprints}
            title="Steps"
            pct={steps != null ? steps / STEPS_GOAL : 0}
            values={
              steps != null ? (
                <>
                  <span className="goal-overview-current">{formatInt(steps)}</span>
                  <span className="goal-overview-sep">/</span>
                  {formatInt(STEPS_GOAL)}
                  <span className="goal-overview-unit">last logged day</span>
                </>
              ) : (
                'No health data yet'
              )
            }
            link="Health"
          />
          <GoalCard
            to="/diet"
            icon={Salad}
            title="Diet"
            pct={dietGoal.target > 0 ? dietGoal.current / dietGoal.target : 0}
            values={
              <>
                <span className="goal-overview-current">{dietGoal.current}</span>
                <span className="goal-overview-sep">/</span>
                {dietGoal.target}
                <span className="goal-overview-unit">{dietGoal.unit}</span>
              </>
            }
            link="Diet"
          />
          <GoalCard
            to="/medical"
            icon={Stethoscope}
            title="Medical"
            pct={medicalGoal.target > 0 ? medicalGoal.current / medicalGoal.target : 0}
            values={
              <>
                <span className="goal-overview-current">{medicalGoal.current}</span>
                <span className="goal-overview-sep">/</span>
                {medicalGoal.target}
                <span className="goal-overview-unit">{medicalGoal.unit}</span>
              </>
            }
            link="Medical"
          />
        </div>
      </section>

      <section className="overview">
        <h2>This week</h2>
        <div className="stats">
          <div className="stat">
            <div className="value">{week.length}</div>
            <span className="label">Workouts</span>
          </div>
          <div className="stat">
            <div className="value">{weekKm.toFixed(1)}</div>
            <span className="label">Cardio km</span>
          </div>
          <div className="stat">
            <div className="value">{weekVolT.toFixed(1)}</div>
            <span className="label">Strength volume, t</span>
          </div>
          <div className="stat">
            <div className="value">{steps != null ? formatInt(steps) : '—'}</div>
            <span className="label">Steps · last logged day</span>
          </div>
        </div>
      </section>
    </>
  );
}
